import React, { useEffect, useState } from "react";
import { Outlet, matchPath, useLocation, useNavigate } from "react-router-dom";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import useCustomerQueryCaa from "@repo/shopify/queries/useCustomerQueryCaa";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";
import Select from "@atoms/Select";

import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useCustomerQuery from "@hooks/useCustomerQuery";
import useHeaderScroll from "@hooks/useHeaderScroll";
import useLanguage from "@hooks/useLanguage";

import apolloCaaClient from "@lib/apolloCaa";
import { hikoLogout } from "@lib/hikoHeadlessSession";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

const LINKS = [
  { href: URLS.ORDER_HISTORY, label: "Order History" },
  { href: URLS.ACCOUNT, label: "Account" },
  { href: URLS.ADDRESSES, label: "Addresses" },
];

const LOGOUT_VALUE = "logout";

const BACK_NAV_PATTERNS = [URLS.ORDER_DETAIL, URLS.RETURN, URLS.RETURN_REQUEST];

export default function Account() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { lang } = useLanguage();
  const { isPC } = useCheckDevice();

  const showBackButton = BACK_NAV_PATTERNS.some((pattern) => matchPath(pattern, pathname));

  const { fetchCustomer } = useCustomerQuery();
  const { fetchCaaCustomer } = useCustomerQueryCaa({ client: apolloCaaClient });
  const { customer, caaToken, logout, token, updateCaa } = useCustomerStore();
  const { openToast, setMeta, openAccount } = useLayoutStore();

  useHeaderScroll();

  /** 계정 내 전체 주문 라인아이템 개수 (환불 상품 제외) */
  const [sessionChecked, setSessionChecked] = useState(false);

  const hasSession = lang === "jp" ? Boolean(caaToken?.accessToken) : Boolean(token.accessToken);

  // 로그아웃된 상황에서 접근했을 때
  useEffect(() => {
    if (!hasSession) {
      navigate(URLS.HOME);
      setTimeout(() => {
        openAccount(true);
      }, 500);
    }

    window.scrollTo(0, 0);
  }, []);

  // 세션이 바뀔 때만 검증
  useEffect(() => {
    setSessionChecked(false);
  }, [hasSession]);

  // 세션이 만료되었을때
  useEffect(() => {
    // 조회 실패(리프레시 실패 등) 또는 customer가 없는 경우 — Loading에 갇히지 않도록 명시적으로 로그아웃 처리
    const handleSessionExpired = () => {
      openToast(NOTIFICATIONS.WARNING_END_SESSION);
      logout();
      navigate(URLS.HOME);
      setTimeout(() => {
        openAccount(true);
      }, 300);
    };

    if (lang === "jp") {
      if (!caaToken?.accessToken) return;
      void fetchCaaCustomer()
        .then(({ data }) => {
          if (data?.customer) {
            updateCaa(data.customer);
            setSessionChecked(true);
          } else {
            handleSessionExpired();
          }
        })
        .catch(handleSessionExpired);
      return;
    }

    void fetchCustomer({ variables: { customerAccessToken: token.accessToken } })
      .then(({ data }) => {
        if (data?.customer) setSessionChecked(true);
        else handleSessionExpired();
      })
      .catch(handleSessionExpired);
  }, [token, caaToken, pathname]);

  useEffect(() => {
    setMeta({ title: `Account - ${DEFAULT_TITLE}` });
  }, []);

  const handleLogout = async () => {
    // jp는 Custom(Hiko) identity provider라 Hiko 브로커에 별도 세션이 있으므로, 로컬 상태만
    // 지우면 실제로는 로그아웃되지 않음 — el.logout()이 Shopify 로그아웃까지 처리하도록 위임한다.
    const isHikoSession = caaToken?.source === "hiko";

    logout();
    openToast(NOTIFICATIONS.SUCCESS_LOGOUT);

    if (lang === "jp" && isHikoSession) {
      try {
        await hikoLogout();
        // 문서/소스상으론 el.logout()이 응답의 logoutUrl로 location.assign()까지 해준다고 되어
        // 있으나, 실제로는 Shopify로 가는 네비게이션이 전혀 관측되지 않아(Network 탭에
        // headless/logout 요청 이후 아무 것도 안 잡힘) 화면이 멈춰있는 문제가 있었다. 그래서
        // hikoLogout() 완료 후 여기서 명시적으로 홈으로 이동시킨다 — 브로커/Shopify 세션이
        // 실제로 끊기는지는 별개 문제(Hiko에 리포트됨)이고, 최소한 우리 앱 UX는 멈추지 않게 함.
      } catch {
        // 위젯 로그아웃 자체가 실패한 경우(네트워크 등)에도 로컬 로그아웃은 이미 처리됐으므로 동일하게 폴백
      }
    }

    navigate(URLS.HOME);
  };

  return (
    <>
      <section className="pc:col-span-2 pc:top-30 sticky top-0 z-10 col-span-8 flex h-fit flex-col gap-1 pb-2.5" id="filter">
        {isPC ? (
          <>
            {LINKS.map(({ href, label }) => {
              if (customer.tags.includes("PARTNERSHIP_TOSS") && label === "Addresses") return;
              return (
                <Link
                  as="text"
                  className="pc:flex hidden"
                  href={href}
                  isActive={pathname === href || (pathname.includes("order") && href.includes("order"))}
                  key={href}
                >
                  {label}
                </Link>
              );
            })}
            <Button className="pc:flex hidden" isActive={false} onClick={handleLogout}>
              Logout
            </Button>
          </>
        ) : (
          <>
            {showBackButton ? (
              <div className="pc:hidden py-2.5">
                <Button
                  as="text"
                  className="items-center gap-2"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <Icon className="h-3.5" name="LeftArrow" />
                  <span className="text-lg">Back</span>
                </Button>
              </div>
            ) : (
              <div className="pc:hidden flex items-center gap-2 bg-white py-2.5">
                <span className="font-fuzzy-bold text-base! leading-none">Viewing:</span>
                <Select
                  as="square"
                  onChange={(e) => {
                    if (e.target.value === LOGOUT_VALUE) {
                      void handleLogout();
                      return;
                    }
                    navigate(e.target.value);
                  }}
                  options={[
                    ...LINKS.filter(({ label }) => !(customer.tags.includes("PARTNERSHIP_TOSS") && label === "Addresses")).map(
                      ({ href, label }) => ({ name: label, value: href }),
                    ),
                    { name: "Logout", value: LOGOUT_VALUE },
                  ]}
                  value={pathname}
                  variant="secondary"
                />
              </div>
            )}
          </>
        )}
      </section>

      <section className="pc:col-span-10 col-span-8 grid grid-cols-subgrid">
        {sessionChecked ? <Outlet /> : <Loading type="page" />}
      </section>
    </>
  );
}
